import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

// ============================================================================
// DA COMPILARE PRIMA DELLA PUBBLICAZIONE
// ----------------------------------------------------------------------------
// Finche' questi campi contengono il valore segnaposto, la pagina mostra un
// avviso in evidenza e non deve essere considerata pubblicabile.
// Sostituire ogni "DA COMPILARE" con il dato reale.
//
// Il resto del testo descrive fedelmente i trattamenti realmente svolti dalla
// piattaforma (verificati sul codice ad agosto 2026): account e auto-
// registrazione, richieste di contatto, attivita' formativa, questionario di
// gradimento, ordini e attestati. Tutti i fornitori sono in UE/SEE, non c'e'
// pagamento online attivo e non si usano cookie di profilazione.
// ============================================================================
const TITOLARE = {
  ragioneSociale: "Burocratica Servizi S.r.l.",
  sedeLegale: "Corso Enrico Tazzoli 215/12A, 10137 Torino (TO)",
  partitaIva: "11628240019",
  emailPrivacy: "amministrazione@burocraticaservizi.it",
  pec: "burocraticaservizisrl@pec.it",
};

// Data dell'ultima revisione del testo (aggiornare a ogni modifica sostanziale).
const ULTIMO_AGGIORNAMENTO = "3 agosto 2026";

const DA_COMPILARE = "DA COMPILARE";
const incompleta = Object.values(TITOLARE).some((v) => v === DA_COMPILARE);

function Sezione({ titolo, children }) {
  return (
    <section className="mb-10">
      <h2 className="text-xl sm:text-2xl text-brand-ink mb-3">{titolo}</h2>
      <div className="text-brand-muted leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

export default function PrivacyPolicy() {
  return (
    <div data-testid="privacy-page" className="min-h-screen bg-white">
      <header className="border-b border-brand-line">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-brand-muted hover:text-brand-blue transition-colors">
            <ArrowLeft size={16} />
            Torna alla home
          </Link>
          <img
            src={`${process.env.PUBLIC_URL}/logo-academy-full.png`}
            alt="Buro Academy"
            className="h-7 w-auto"
          />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        {incompleta && (
          <div
            data-testid="privacy-incomplete-warning"
            className="mb-10 border-l-4 border-brand-gold bg-brand-gold/10 px-4 py-3 text-sm text-brand-ink"
          >
            <strong>Bozza non pubblicabile.</strong> I dati del titolare del
            trattamento non sono ancora stati inseriti. Completare il blocco{" "}
            <code>TITOLARE</code> in cima a questo file e far validare il testo
            da un professionista prima di rendere raggiungibile la pagina.
          </div>
        )}

        <h1 className="text-3xl sm:text-4xl text-brand-ink mb-3">
          Informativa sul trattamento dei dati personali
        </h1>
        <p className="text-brand-muted mb-2">
          Questa informativa descrive come Buro Academy tratta i dati personali
          di chi utilizza la piattaforma, ai sensi del Regolamento (UE) 2016/679
          (&ldquo;GDPR&rdquo;).
        </p>
        <p className="text-sm text-brand-muted mb-10">
          Ultimo aggiornamento: {ULTIMO_AGGIORNAMENTO}.
        </p>

        <Sezione titolo="Titolare del trattamento">
          <p>
            {TITOLARE.ragioneSociale}, con sede in {TITOLARE.sedeLegale},
            partita IVA {TITOLARE.partitaIva}.
          </p>
          <p>
            Per esercitare i propri diritti o per qualsiasi domanda su questa
            informativa è possibile scrivere a{" "}
            <a href={`mailto:${TITOLARE.emailPrivacy}`} className="text-brand-blue hover:underline">
              {TITOLARE.emailPrivacy}
            </a>
            {TITOLARE.pec && <> oppure alla PEC {TITOLARE.pec}</>}.
          </p>
        </Sezione>

        <Sezione titolo="Quali dati trattiamo">
          <p>
            <strong>Dati dell'account (privati).</strong> Chi si registra come
            privato — o per cui l'amministrazione crea un'utenza — fornisce nome,
            cognome, indirizzo email, codice fiscale, data di nascita e, se
            vuole, un numero di telefono. Il codice fiscale e la data di nascita
            sono necessari perché vengono riportati sugli attestati.
          </p>
          <p>
            <strong>Dati dell'account (aziende).</strong> Chi si registra come
            azienda fornisce i dati dell'organizzazione utili alla fatturazione —
            ragione sociale, partita IVA, codice fiscale, codice destinatario
            (SDI), PEC, telefono e indirizzo di fatturazione — insieme
            all'indirizzo email e alla password usati per accedere. Il referente
            aziendale può a sua volta creare le utenze dei propri discenti (nome,
            cognome, codice fiscale, data di nascita ed eventuale telefono); i
            discenti accedono con il proprio codice fiscale.
          </p>
          <p>
            <strong>Verifica dell'indirizzo email.</strong> Per le registrazioni
            autonome inviamo un'email di conferma all'indirizzo indicato, per
            verificarne la titolarità prima di attivare l'accesso.
          </p>
          <p>
            <strong>Richieste di contatto.</strong> Chi compila il modulo di
            contatto ci fornisce nome, email, il corso di interesse e il testo
            del messaggio, oltre a un numero di telefono e a un eventuale
            allegato in PDF (ad esempio il modulo di iscrizione compilato), se
            sceglie di indicarli.
          </p>
          <p>
            <strong>Attività sulla piattaforma.</strong> Registriamo a quali
            corsi l'utente è iscritto, quanti secondi di ciascuna lezione ha
            effettivamente visto, le risposte date ai test, i punteggi ottenuti
            e il numero di tentativi. Questi dati servono a stabilire se il
            percorso è stato completato e se l'attestato può essere emesso.
          </p>
          <p>
            <strong>Questionario di gradimento.</strong> Al termine del corso
            l'utente compila un breve questionario (alcuni giudizi numerici e un
            commento facoltativo). Serve a emettere l'attestato e a migliorare i
            corsi; chi eroga la formazione ne consulta i risultati in forma
            aggregata.
          </p>
          <p>
            <strong>Ordini.</strong> Quando viene creato un ordine conserviamo
            il suo contenuto (corsi e posti) e i dati di fatturazione
            dell'acquirente. Il pagamento non avviene online sulla piattaforma:
            l'ordine viene evaso dall'amministrazione. Eventuali dati bancari
            o di pagamento sono quindi trattati al di fuori della piattaforma.
          </p>
          <p>
            <strong>Attestati.</strong> Conserviamo gli attestati emessi, con il
            relativo codice di verifica.
          </p>
          <p>
            <strong>Dati tecnici.</strong> Per proteggere i moduli pubblici da
            abusi (registrazione, contatto, recupero password) il sistema
            registra temporaneamente l'indirizzo IP di provenienza, al solo fine
            di limitare il numero di invii ravvicinati. Non lo usiamo per
            profilare gli utenti.
          </p>
        </Sezione>

        <Sezione titolo="Perché li trattiamo e su quale base">
          <p>
            I dati dell'account, dell'attività formativa e del questionario di
            gradimento servono a erogare il servizio di formazione e a emettere
            gli attestati: la base giuridica è l'<strong>esecuzione del
            contratto</strong> (o l'adozione di misure precontrattuali richieste
            dall'interessato, come la registrazione).
          </p>
          <p>
            I dati delle richieste di contatto sono trattati sulla base del{" "}
            <strong>consenso</strong> che viene richiesto al momento dell'invio,
            e servono unicamente a rispondere alla richiesta.
          </p>
          <p>
            La conservazione degli ordini e dei documenti di fatturazione, oltre
            che degli attestati, risponde a <strong>obblighi di legge</strong>
            {" "}(in particolare fiscali e contabili).
          </p>
          <p>
            Il trattamento temporaneo dell'indirizzo IP a fini anti-abuso e la
            sicurezza della piattaforma si fondano sul{" "}
            <strong>legittimo interesse</strong> del titolare a garantire un
            funzionamento corretto e sicuro del servizio.
          </p>
        </Sezione>

        <Sezione titolo="A chi arrivano i dati">
          <p>
            <strong>Video delle lezioni.</strong> I video sono ospitati
            direttamente sull'infrastruttura della piattaforma e trasmessi dai
            suoi stessi server: <strong>nessun fornitore video esterno</strong>{" "}
            (come YouTube o Vimeo) riceve i dati di chi guarda le lezioni.
          </p>
          <p>
            <strong>Google Fonts.</strong> I caratteri tipografici della
            piattaforma sono caricati dai server di Google, che di conseguenza
            ricevono l'indirizzo IP a ogni visita.
          </p>
          <p>
            <strong>Immagini della vetrina pubblica.</strong> Alcune immagini
            delle pagine pubbliche sono caricate da un servizio esterno di
            fototeca (Pexels), che di conseguenza riceve l'indirizzo IP di chi
            visita quelle pagine.
          </p>
          <p>
            <strong>Fornitore dell'infrastruttura e dei servizi tecnici.</strong>{" "}
            I dati risiedono sui server che ospitano la piattaforma; ci
            avvaliamo inoltre di un fornitore di posta elettronica per l'invio
            delle email di servizio (verifica dell'indirizzo e recupero
            password). Questi fornitori agiscono come responsabili del
            trattamento, sulla base di appositi accordi, e trattano i dati{" "}
            <strong>all'interno dell'Unione Europea / Spazio Economico Europeo</strong>.
            L'elenco aggiornato dei responsabili è disponibile su richiesta al
            titolare.
          </p>
          <p>
            Non vendiamo né cediamo i dati a terzi per finalità commerciali.
          </p>
        </Sezione>

        <Sezione titolo="Cookie e tecnologie simili">
          <p>
            La piattaforma <strong>non utilizza cookie di profilazione o di
            tracciamento</strong>, né strumenti di analisi di terze parti.
          </p>
          <p>
            Per tenere l'utente autenticato dopo l'accesso, il browser conserva
            localmente un token tecnico di sessione (nell'area di archiviazione
            del sito). È indispensabile al funzionamento dell'area riservata,
            non serve a seguire la navigazione e viene rimosso al logout.
          </p>
        </Sezione>

        <Sezione titolo="Verifica pubblica degli attestati">
          <p>
            Ogni attestato riporta un codice e un QR code che rimandano a una
            pagina di verifica accessibile senza autenticazione. Chi dispone del
            codice può vedere <strong>nome, cognome, titolo del corso e data di
            emissione</strong> della persona a cui l'attestato è intestato.
          </p>
          <p>
            È il meccanismo che permette a un committente di accertare
            l'autenticità di un attestato. Chi non desidera questa
            visibilità può chiederne la revoca scrivendo al titolare.
          </p>
        </Sezione>

        <Sezione titolo="Per quanto tempo li conserviamo">
          <p>
            I dati dell'account e dell'attività formativa sono conservati per
            tutta la durata del rapporto e, successivamente, per il tempo
            necessario ad assolvere gli obblighi di legge.
          </p>
          <p>
            Gli ordini e i documenti di fatturazione sono conservati per il
            periodo previsto dagli obblighi fiscali e contabili.
          </p>
          <p>
            Gli attestati e i dati necessari alla loro verifica sono conservati
            finché l'attestato resta valido.
          </p>
          <p>
            Le richieste di contatto e i relativi allegati sono conservati per
            il tempo necessario a gestirle e per il periodo successivo utile a
            dimostrare di avervi dato seguito.
          </p>
        </Sezione>

        <Sezione titolo="I diritti dell'interessato">
          <p>
            È possibile chiedere in qualsiasi momento di accedere ai propri
            dati, di correggerli, di cancellarli, di limitarne il trattamento,
            di riceverli in formato leggibile da un computer e di opporsi al
            trattamento. Dove il trattamento si fonda sul consenso, il consenso
            può essere revocato in qualsiasi momento, senza che ciò pregiudichi
            quanto trattato prima della revoca.
          </p>
          <p>
            Le richieste vanno inviate a{" "}
            <a href={`mailto:${TITOLARE.emailPrivacy}`} className="text-brand-blue hover:underline">
              {TITOLARE.emailPrivacy}
            </a>
            .
          </p>
          <p>
            Chi ritenga che il trattamento dei propri dati violi il Regolamento
            può proporre reclamo al Garante per la protezione dei dati
            personali.
          </p>
        </Sezione>
      </main>

      <footer className="border-t border-brand-line py-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-sm text-brand-muted">
          © {new Date().getFullYear()} Burocratica Servizi — Tutti i diritti riservati
        </div>
      </footer>
    </div>
  );
}
